/*
MIT LICENSE
Copyright (c) 2020-present Alibaba Cloud Design
Copied from 'antd/lib/upload/UploadList/listitem.tsx';
Copyright (c) 2015-present Ant UED, https://xtech.antfin.com/

Permission is hereby granted, free of charge, to any person obtaining
a copy of this software and associated documentation files (the
"Software"), to deal in the Software without restriction, including
without limitation the rights to use, copy, modify, merge, publish,
distribute, sublicense, and/or sell copies of the Software, and to
permit persons to whom the Software is furnished to do so, subject to
the following conditions:

The above copyright notice and this permission notice shall be
included in all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND,
EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND
NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE
LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION
WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE. */
import React from 'react';
import CSSMotion from 'rc-motion';
import classNames from 'classnames';
import { Tooltip } from '../../tooltip';
import { Progress } from '../../progress';
import { ConfigContext } from '../../config-provider';
import { Icon } from '../../icon';
const ListItem = React.forwardRef(({ prefixCls, className, style, locale, listType, file, items, progress: progressProps, iconRender, actionIconRender, itemRender, isImgUrl, showPreviewIcon, showRemoveIcon, showDownloadIcon, removeIcon: customRemoveIcon, downloadIcon: customDownloadIcon, onPreview, onDownload, onClose, type }, ref) => {
    // Delay to show the progress bar
    const [showProgress, setShowProgress] = React.useState(false);
    const progressRafRef = React.useRef();
    // console.log(showPreviewIcon, 'showPreviewIcon');
    React.useEffect(() => {
        progressRafRef.current = setTimeout(() => {
            setShowProgress(true);
        }, 300);
        return () => {
            window.clearTimeout(progressRafRef.current);
        };
    }, []);
    // This is used for legacy span make scrollHeight the wrong value.
    // We will force these to be `display: block` with non `picture-card`
    const spanClassName = `${prefixCls}-span`;
    const iconNode = iconRender(file);
    const infoUploadingClass = classNames({
        [`${prefixCls}-list-item`]: true,
        [`${prefixCls}-list-item-${file.status}`]: true,
        [`${prefixCls}-list-item-list-type-${listType}`]: true
    });
    const linkProps = typeof file.linkProps === 'string' ? JSON.parse(file.linkProps) : file.linkProps;
    const removeIcon = showRemoveIcon
        ? actionIconRender((typeof customRemoveIcon === 'function' ? customRemoveIcon(file) : customRemoveIcon) ||
            (file.status === 'uploading' ? (React.createElement(Icon, { type: 'close', size: 'small' })) : (React.createElement(Icon, { type: 'trash-can--outline', size: 'small' }))), () => onClose(file), prefixCls, locale.removeFile, 'remove')
        : null;
    const downloadIcon = showDownloadIcon && file.status === 'done'
        ? actionIconRender((typeof customDownloadIcon === 'function' ? customDownloadIcon(file) : customDownloadIcon) || (React.createElement(Icon, { type: 'download', size: 'small' })), () => onDownload(file), prefixCls, locale.downloadFile, 'download')
        : null;
    const previewStyle = {
        pointerEvents: 'none',
        opacity: 0.5
    };
    const previewIcon = showPreviewIcon ? (React.createElement("a", { href: file.url || file.thumbUrl, target: '_blank', rel: 'noopener noreferrer', style: file.url || file.thumbUrl ? undefined : previewStyle, onClick: (e) => onPreview(file, e), title: locale.previewFile },
        React.createElement(Icon, { type: 'view--outline', size: 'small', className: `${prefixCls}-list-item-view-icon` }))) : null;
    const successIcon = (React.createElement(Icon, { type: 'checkmark--filled', size: 'small', className: `${prefixCls}-list-item-success-icon` }));
    const warningIcon = (React.createElement(Icon, { type: 'warning--outline', size: 'small', className: `${prefixCls}-list-item-error-icon` }));
    const downloadOrDelete = listType !== 'picture-card' && (React.createElement("span", { key: 'download-delete', className: classNames(`${prefixCls}-list-item-card-actions`, {
            picture: listType === 'picture'
        }) },
        (file.status === 'success' || (file.status === 'done' && type === 'simple')) && successIcon,
        file.status === 'error' && warningIcon,
        downloadIcon,
        removeIcon));
    const listItemNameClass = classNames(`${prefixCls}-list-item-name`);
    const preview = file.url
        ? [
            React.createElement("a", { key: 'view', target: '_blank', rel: 'noopener noreferrer', className: listItemNameClass, title: file.name, ...linkProps, href: file.url, onClick: (e) => onPreview(file, e) }, file.name),
            downloadOrDelete
        ]
        : [
            React.createElement("span", { key: 'view', className: listItemNameClass, onClick: (e) => onPreview(file, e), title: file.name }, file.name),
            downloadOrDelete
        ];
    const actions = listType === 'picture-card' &&
        (file.status !== 'uploading' ? (React.createElement("span", { className: `${prefixCls}-list-item-actions` },
            previewIcon,
            file.status === 'done' && downloadIcon,
            removeIcon)) : // <span className={`${prefixCls}-list-item-actions`}>{cancelText}</span>
            null);
    if (listType === 'picture-card') {
        // console.log(removeIcon, showRemoveIcon, file.status);
    }
    let message;
    if (file.response && typeof file.response === 'string') {
        message = file.response;
    }
    else {
        message = file.error?.statusText || file.error?.message || locale.uploadError;
    }
    let icon = React.createElement("div", { className: `${prefixCls}-text-icon` }, iconNode);
    if (listType === 'picture' || listType === 'picture-card') {
        if (file.status === 'uploading' || (!file.thumbUrl && !file.url)) {
            const uploadingClassName = classNames({
                [`${prefixCls}-list-item-thumbnail`]: true,
                [`${prefixCls}-list-item-file`]: file.status !== 'uploading'
            });
            if (listType === 'picture') {
                icon = React.createElement("div", { className: uploadingClassName }, iconNode);
            }
            else if (listType === 'picture-card') {
                icon = (React.createElement("div", { className: uploadingClassName },
                    iconNode,
                    preview));
            }
        }
        else {
            const thumbnail = isImgUrl?.(file) ? (React.createElement("img", { src: file.thumbUrl || file.url, alt: file.name, className: `${prefixCls}-list-item-image` })) : (iconNode);
            const aClassName = classNames({
                [`${prefixCls}-list-item-thumbnail`]: true,
                [`${prefixCls}-list-item-file`]: isImgUrl && !isImgUrl(file)
            });
            icon = (React.createElement("a", { className: aClassName, onClick: (e) => onPreview(file, e), href: file.url || file.thumbUrl, target: '_blank', rel: 'noopener noreferrer' }, thumbnail));
        }
    }
    let iconAndPreview = (React.createElement("span", { className: spanClassName },
        icon,
        preview));
    if (listType === 'picture-card') {
        iconAndPreview = React.createElement("span", { className: spanClassName }, icon);
    }
    const { getPrefixCls } = React.useContext(ConfigContext);
    const rootPrefixCls = getPrefixCls();
    const dom = (React.createElement("div", { className: infoUploadingClass },
        React.createElement("div", { className: `${prefixCls}-list-item-info` }, iconAndPreview),
        actions,
        showProgress && (React.createElement(CSSMotion, { motionName: `${rootPrefixCls}-fade`, visible: file.status === 'uploading', motionDeadline: 1000 }, ({ className: motionClassName }) => {
            // show loading icon if upload progress listener is disabled
            const loadingProgress = 'percent' in file ? (
            // @ts-ignore
            React.createElement(Progress, { ...progressProps, type: 'line', percent: file.percent })) : null;
            return (React.createElement("div", { className: classNames(`${prefixCls}-list-item-progress`, motionClassName) }, loadingProgress));
        }))));
    const listContainerNameClass = type === 'simple'
        ? classNames(`${prefixCls}-list-${listType}-container ${prefixCls}-simple-container`, className)
        : classNames(`${prefixCls}-list-${listType}-container`, className);
    const item = file.status === 'error' ? (React.createElement(Tooltip, { title: message, getPopupContainer: (node) => node.parentNode }, dom)) : (dom);
    return (React.createElement("div", { className: listContainerNameClass, style: style, ref: ref }, itemRender
        ? itemRender(item, file, items, {
            download: onDownload.bind(null, file),
            preview: onPreview.bind(null, file),
            remove: onClose.bind(null, file)
        })
        : item));
});
export default ListItem;
